import { isFocusable } from 'tabbable';
import { findClosestFocusable } from './focus.js';
import { mac, playwright, android } from './platform.js';
import { fromClientXY, isInteger, fromElementCenter, isWithinRoundingDistance } from './vector.js';

/**
 * A temporary solution to preventing fullscreen from eating events on our elements. We should
 * eventually figure out how to do this from outside of fpl.
 * https://app.asana.com/0/0/1207289930014758/f
 */
const PREVENT_EVENT_CAPTURE_ATTR = 'data-fullscreen-prevent-event-capture';
const PREVENT_EVENT_CAPTURE = Object.freeze({ [PREVENT_EVENT_CAPTURE_ATTR]: 'true' });
const PREVENT_KEY_CAPTURE_ATTR = 'data-fullscreen-prevent-event-capture-keys';
const PREVENT_KEY_CAPTURE = Object.freeze({ [PREVENT_KEY_CAPTURE_ATTR]: 'true' });
const PREVENT_WHEEL_CAPTURE_ATTR = 'data-fullscreen-wheel-event-capture';
const PREVENT_WHEEL_CAPTURE = Object.freeze({ [PREVENT_WHEEL_CAPTURE_ATTR]: 'true' });
const WHEEL_EVENT_PASSTHROUGH_ATTR = 'data-fullscreen-wheel-event-passthrough';
const WHEEL_EVENT_PASSTHROUGH = Object.freeze({ [WHEEL_EVENT_PASSTHROUGH_ATTR]: 'true' });
/**
 * Use our terminology for event handling - accept means you have decided to handle the event and
 * expect nothing else to come of it.
 *
 * TODO: should only certain events be acceptable? For example, should it be considered a bug
 * to accept a `pointermove` event. So far I have only been using this for keyboard events.
 */
function accept(e) {
    e.preventDefault();
    e.stopPropagation();
}
/**
 * Intended to be called as `if (forward(e, cb)) return`. Calls the callback with the event,
 * and returns whether the default was prevented.
 */
function forward(e, cb) {
    cb?.(e);
    return e.defaultPrevented;
}
function preventDefault(e) {
    e.preventDefault();
}
function stopPropagation(e) {
    e.stopPropagation();
}
/**
 * Check if a click event was triggered by a keyboard. Keyboard clicks have their `clientX` and
 * `clientY` values set to 0.
 */
function isKeyboardClick(e) {
    return e.clientX === 0 && e.clientY === 0;
}
/**
 * Check if the event's `target` is the same as the `currentTarget`. This can be used
 * to determine if the event was triggered on the element that the event listener was
 * attached to, vs. a descendant element.
 */
function targetIsCurrent(e) {
    return e.target === e.currentTarget;
}
/**
 * Determines if the event came through a react portal by checking if the
 * target is not a descendant of the element the event listener is attached to.
 */
function isPortaledEvent(e) {
    return !e.currentTarget.contains(e.target);
}
function addListener(el, type, listener, options) {
    if (el) {
        el.addEventListener(type, listener, options);
        return () => el.removeEventListener(type, listener, options);
    }
}
/**
 * Check if a `pointerdown` event was triggered by a screen reader click (VO + space).
 *
 * @note This *only* works for `pointerdown` events, `pointerup` events always have 0 width/height.
 *
 * Borrowed from @react-aria/utils and modified.
 * @see https://github.com/adobe/react-spectrum/blob/da4ef76444b9957d1dc18fc36b4ed9405c68f7fb/packages/%40react-aria/utils/src/isVirtualEvent.ts#L42
 *
 * TODO: figure out how to provide attribution properly with license etc.
 * https://figma.slack.com/archives/C08A57KUVA9/p1756312808207749
 */
function isVirtualPointerDownEvent(event) {
    // don't classify test pointerdown events as virtual
    if (process.env.NODE_ENV === 'test')
        return false;
    const e = asNativeEvent(event);
    // In MacOS VoiceOver we can only really use the center clicking to determine if
    // a user is a voice over user or not.
    //
    // Also use this logic for Playwright so that tests are able to simulate virtual vs. real
    // pointerdown events by passing a `position` other than the center of the target.
    if ((mac || playwright) && e.target instanceof Element) {
        const client = fromClientXY(e);
        // VoiceOver clicks (ctrl+opt+space) are always integer values, while an actual
        // pointing device will almost always have a long fractional value.
        if (!isInteger(client))
            return false;
        let el = e.target;
        while (el) {
            const center = fromElementCenter(el);
            // VoiceOver can sometimes use the floor or ceiling value seemingly based on the aspect ratio
            // of the element. So we ensure that it's both larger than the floor and smaller than the ceil.
            if (isWithinRoundingDistance(client, center))
                return true;
            // Also try the first focusable ancestor, screen reader clicks sometimes set a child element
            // as the target even though they compute the center of the first focusable ancestor.
            el = isFocusable(el) ? null : findClosestFocusable(el);
        }
        return false;
    }
    return ((!android && e.width === 0 && e.height === 0) ||
        // Android TalkBack double tap sometimes returns an event with width and height of 1 and
        // pointerType === 'mouse' so we need to check for a specific combination of event attributes.
        (e.width === 1 &&
            e.height === 1 &&
            // Cannot use "event.pressure === 0" alone due to Safari pointer events always returning
            // pressure === 0 instead of .5, see https://bugs.webkit.org/show_bug.cgi?id=206216.
            e.pressure === 0 &&
            e.detail === 0 &&
            // distinguishes Talkback double tap from Windows Firefox touch screen press
            e.pointerType === 'mouse'));
}
const UNHANDLED_EVENTS = new WeakSet();
/**
 * Mark an event as unhandled. This is meant for events that have native browser behavior on a
 * specific target element, but that we want to mark as _not_ handled by the browser. For example,
 * the browser normally handles ⌘Z on inputs, but unmodified inputs may want to mark this event as
 * unhandled, which can inform a caller that they are allowed to handle the event.
 */
function unhandle(e) {
    UNHANDLED_EVENTS.add(asNativeEvent(e));
}
/**
 * Check if an event has been marked as unhandled. This means the event would normally
 * be inferred as handled by the browser, but it was explicitly marked as unhandled.
 */
function isEventUnhandled(e) {
    return UNHANDLED_EVENTS.has(asNativeEvent(e));
}
function asNativeEvent(e) {
    return ('nativeEvent' in e ? e.nativeEvent : e);
}
/**
 * Install a global Escape key listener that calls the provided callback when Escape is pressed.
 * Returns a cleanup function that removes the listener. The event is accepted by default, unless
 * `false` is returned from the callback. The callback is in capture phase to give priority to this
 * newly registered listener.
 */
function handleEscape(onEscape) {
    function listener(e) {
        if (e.key === 'Escape' && onEscape() !== false)
            accept(e);
    }
    return addListener(document, 'keydown', listener, true);
}

export { PREVENT_EVENT_CAPTURE, PREVENT_EVENT_CAPTURE_ATTR, PREVENT_KEY_CAPTURE, PREVENT_KEY_CAPTURE_ATTR, PREVENT_WHEEL_CAPTURE, PREVENT_WHEEL_CAPTURE_ATTR, WHEEL_EVENT_PASSTHROUGH, WHEEL_EVENT_PASSTHROUGH_ATTR, accept, addListener, asNativeEvent, forward, handleEscape, isEventUnhandled, isKeyboardClick, isPortaledEvent, isVirtualPointerDownEvent, preventDefault, stopPropagation, targetIsCurrent, unhandle };
//# sourceMappingURL=event.js.map
